import { useState } from 'react'
import axios from 'axios'
import { useParams, Link } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getDevice,
  getHealthHistory,
  listRingGroups,
  listGSMPatterns,
  listReleases,
  listOrganizations,
  pushConfig,
  pushUpdate,
  updateDevice,
  disableDevice,
  enableDevice,
  enableDeviceSipTLS,
  disableDeviceSipTLS,
  regenerateDeviceSipTLSCert,
  caCertDownloadURL,
  enableDeviceSrtpBridging,
  disableDeviceSrtpBridging,
  syncDeviceCredentials,
  enableDeviceSipAuth,
  disableDeviceSipAuth,
  updateDeviceConfig,
  updateDevicePerformance,
  updateDeviceDNSFailover,
  upsertRingGroup,
  deleteRingGroup,
  upsertGSMPattern,
  deleteGSMPattern,
  listNetworkSegments,
  upsertNetworkSegment,
  deleteNetworkSegment,
  syncDeviceQueues,
  maxNetworkSegments,
  listPlatforms,
  listPlatformDomains,
  runDeviceTraceroute,
  Device,
  HealthReport,
  Organization,
  RingGroup,
  GSMPattern,
  NetworkSegment,
  Release,
  UpdateType,
  Platform,
  NetsapiensDomain,
  TracerouteResult,
} from '../api/client'
import StatusBadge from '../components/StatusBadge'
import SearchableSelect from '../components/SearchableSelect'
import { useAuth } from '../auth/AuthContext'

type Tab = 'overview' | 'ring-groups' | 'gsm-patterns' | 'network' | 'updates' | 'settings'

function formatUptime(seconds: number): string {
  if (!seconds) return '—'
  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  if (days > 0) return `${days}d ${hours}h`
  if (hours > 0) return `${hours}h ${minutes}m`
  return `${minutes}m`
}

// formatHardware describes the actual physical hardware reported by the
// agent (RAM + NIC count) — lets an operator tell which tier is deployed
// (e.g. the 2GB/single-NIC vs 8GB/dual-NIC plan) without SSHing in. Shows
// raw facts rather than guessing a tier label, since a device could be
// wired up outside the two standard configurations.
function formatHardware(device: Device): string {
  const nics = device.Interfaces?.length ?? 0
  const parts: string[] = []
  if (device.TotalMemoryMB > 0) {
    parts.push(`${(device.TotalMemoryMB / 1024).toFixed(device.TotalMemoryMB % 1024 === 0 ? 0 : 1)} GB RAM`)
  }
  if (nics > 0) {
    parts.push(`${nics} NIC${nics === 1 ? '' : 's'} (${device.Interfaces.join(', ')})`)
  }
  return parts.length > 0 ? parts.join(' · ') : '—'
}

const RING_STRATEGIES = ['Parallel', 'Sequential', 'Round Robin']

const cardStyle: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  padding: 20,
  marginBottom: 16,
}

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
}

const thStyle: React.CSSProperties = {
  padding: '8px 12px',
  textAlign: 'left',
  fontSize: 12,
  fontWeight: 600,
  color: '#6b7280',
  textTransform: 'uppercase',
  borderBottom: '1px solid #e5e7eb',
  background: '#f9fafb',
}

const tdStyle: React.CSSProperties = {
  padding: '10px 12px',
  borderBottom: '1px solid #f3f4f6',
  fontSize: 14,
}

const btnStyle: React.CSSProperties = {
  padding: '6px 14px',
  borderRadius: 6,
  border: 'none',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 500,
}

function OverviewTab({ device }: { device: Device }) {
  const h = device.LastHealth

  return (
    <div>
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 16px', fontSize: 16 }}>Device Info</h3>
        <table style={{ borderCollapse: 'collapse', fontSize: 14 }}>
          <tbody>
            {[
              ['ID', device.ID],
              ['Name', device.Name || '—'],
              ['Site', device.Site || '—'],
              ['Status', <StatusBadge status={device.Disabled ? 'disabled' : device.Status} />],
              ['Agent Version', device.CurrentVersion || '—'],
              ['Config Version', device.ConfigVersion || '—'],
              ['Hardware', formatHardware(device)],
            ].map(([label, value]) => (
              <tr key={String(label)}>
                <td style={{ padding: '6px 16px 6px 0', color: '#6b7280', fontWeight: 500, whiteSpace: 'nowrap' }}>
                  {label}
                </td>
                <td style={{ padding: '6px 0' }}>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {h && (
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 16px', fontSize: 16 }}>Health</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
            {[
              { label: 'Uptime', value: formatUptime(h.UptimeSeconds) },
              { label: 'CPU', value: `${h.CPUPercent.toFixed(1)}%` },
              { label: 'Memory', value: `${h.MemoryPercent.toFixed(1)}%` },
              { label: 'Active Calls', value: String(h.ActiveCalls) },
              { label: 'Registered Endpoints', value: String(h.RegisteredEndpoints) },
              { label: 'SIP Proxy', value: h.KamailioRunning ? 'Running' : 'Stopped' },
              {
                label: 'Cellular Trunk',
                value: !device.GsmTrunkURI ? 'Not Provisioned' : h.GSMTrunkReachable ? 'Reachable' : 'Unreachable',
              },
              { label: 'Media Relay', value: h.RtpengineRunning ? 'Running' : 'Stopped' },
              { label: 'DNS Failover', value: h.DNSFailoverRunning ? 'Running' : 'Stopped' },
              {
                label: 'Primary Service',
                value: !device.PrimarySipURI ? 'Not Configured' : h.PrimaryReachable ? 'Reachable' : 'Unreachable',
              },
            ].map(({ label, value }) => (
              <div key={label} style={{ background: '#f9fafb', borderRadius: 6, padding: '12px 16px' }}>
                <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>
                  {label}
                </div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{value}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <HealthHistorySection deviceId={device.ID} />
      <TracerouteCard device={device} />
    </div>
  )
}

// TracerouteCard runs `mtr` on the device on demand (defaulting to its
// configured Primary SIP Service if no target is given) — an action, not a
// config field, so it lives on the Overview tab rather than Settings.
// Genuinely slower than every other button on this page (mtr's multiple
// probes per hop can take up to ~30s), so it gets its own loading copy
// rather than a plain "Saving…".
function TracerouteCard({ device }: { device: Device }) {
  const [target, setTarget] = useState('')
  const [result, setResult] = useState<TracerouteResult | null>(null)

  const runMut = useMutation({
    mutationFn: () => runDeviceTraceroute(device.ID, target.trim()),
    onSuccess: (res) => setResult(res),
    onError: (e: unknown) => setResult({ success: false, error: e instanceof Error ? e.message : String(e), reportJson: '' }),
  })

  const inputSt: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box',
    padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14,
  }

  let hops: TracerouteHop[] | null = null
  if (result?.success && result.reportJson) {
    try {
      const parsed = JSON.parse(result.reportJson)
      hops = parsed?.report?.hubs ?? null
    } catch {
      hops = null
    }
  }

  return (
    <div style={cardStyle}>
      <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>Network Diagnostics</h3>
      <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 14px' }}>
        Runs an on-demand <code>mtr</code> trace from the device to a target host — useful for
        digging into a degraded or unreachable Primary Service reading above.
      </p>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
        Target (optional)
      </label>
      <input
        style={{ ...inputSt, marginBottom: 10 }}
        placeholder={device.PrimarySipURI ? `will use: ${device.PrimarySipURI}` : 'e.g. core1-atl.example.com'}
        value={target}
        onChange={e => setTarget(e.target.value)}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <button
          style={{ ...btnStyle, background: '#2563eb', color: '#fff' }}
          onClick={() => { setResult(null); runMut.mutate() }}
          disabled={runMut.isPending}
        >
          {runMut.isPending ? 'Running… (this can take up to 30 seconds)' : 'Run Traceroute'}
        </button>
      </div>
      {result && !result.success && (
        <p style={{ fontSize: 13, color: '#dc2626', margin: 0 }}>{result.error || 'Traceroute failed.'}</p>
      )}
      {result?.success && hops && hops.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '4px 8px', color: '#6b7280', fontSize: 11, textTransform: 'uppercase' }}>Hop</th>
                <th style={{ textAlign: 'left', padding: '4px 8px', color: '#6b7280', fontSize: 11, textTransform: 'uppercase' }}>Host</th>
                <th style={{ textAlign: 'left', padding: '4px 8px', color: '#6b7280', fontSize: 11, textTransform: 'uppercase' }}>Loss %</th>
                <th style={{ textAlign: 'left', padding: '4px 8px', color: '#6b7280', fontSize: 11, textTransform: 'uppercase' }}>Avg (ms)</th>
              </tr>
            </thead>
            <tbody>
              {hops.map((hop, i) => (
                <tr key={i} style={{ borderTop: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '4px 8px' }}>{hop.count ?? i + 1}</td>
                  <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{hop.host ?? '?'}</td>
                  <td style={{ padding: '4px 8px' }}>{hop['Loss%'] ?? '—'}</td>
                  <td style={{ padding: '4px 8px' }}>{hop.Avg ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {result?.success && (!hops || hops.length === 0) && (
        <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>No hops returned.</p>
      )}
    </div>
  )
}

// TracerouteHop is a single row of mtr's `--json` report output
// (report.hubs[]) — field names/casing confirmed against a real `mtr
// --report --json` run during implementation (mtr's JSON schema has
// changed across versions historically, so don't assume this without
// checking against whatever version deploy/DEVICE_PROVISIONING.md installs).
interface TracerouteHop {
  count?: number
  host?: string
  'Loss%'?: number
  Avg?: number
}

// Lightweight, dependency-free line chart — the codebase has no charting
// library, and pulling one in for four small sparklines isn't worth it.
//
// The Y axis always starts at 0 (never at the data's own minimum) and is
// labeled with its actual value range — auto-scaling to min..max with no
// labels (the previous behavior) made a CPU line wobbling between 12-15%
// render as a dramatic full-height swing with no way to tell what the
// numbers actually were. fixedMax pins the top of the axis (e.g. 100 for a
// percentage metric) so the scale doesn't jump around between refreshes;
// otherwise it's the data's own max, padded slightly and rounded for a
// less jittery-looking axis label.
function LineChart({ values, color, unit = '', fixedMax }: { values: number[]; color: string; unit?: string; fixedMax?: number }) {
  const width = 280
  const height = 90
  const pad = 4
  const topLabelHeight = 16
  const chartTop = topLabelHeight
  const chartBottom = height - pad

  if (values.length === 0) {
    return (
      <svg width={width} height={height}>
        <text x={width / 2} y={height / 2} textAnchor="middle" fontSize={12} fill="#9ca3af">
          No data
        </text>
      </svg>
    )
  }

  const dataMax = Math.max(...values, 0)
  const max = fixedMax ?? Math.max(niceRoundUp(dataMax), 1)
  const latest = values[values.length - 1]

  const points = values.map((v, i) => {
    const x = values.length === 1 ? width / 2 : pad + (i / (values.length - 1)) * (width - pad * 2)
    const y = chartBottom - (Math.min(v, max) / max) * (chartBottom - chartTop)
    return `${x.toFixed(1)},${y.toFixed(1)}`
  })

  const labelStyle = { fontSize: 10, fill: '#9ca3af' } as const

  return (
    <svg width={width} height={height}>
      <text x={pad} y={10} {...labelStyle}>{formatAxisValue(max)}{unit}</text>
      <text x={pad} y={height - pad} {...labelStyle}>0{unit}</text>
      <line x1={pad} y1={chartBottom} x2={width - pad} y2={chartBottom} stroke="#e5e7eb" strokeWidth={1} />
      <polyline points={points.join(' ')} fill="none" stroke={color} strokeWidth={2} />
      <text x={width - pad} y={10} textAnchor="end" fontSize={11} fontWeight={600} fill={color}>
        {formatAxisValue(latest)}{unit}
      </text>
    </svg>
  )
}

// niceRoundUp rounds a chart's data max up to a slightly-larger "nice"
// number (next multiple of an appropriate step) so the Y-axis label isn't
// a jittery non-round figure that changes shape on every refresh.
function niceRoundUp(value: number): number {
  if (value <= 0) return 1
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)))
  const step = magnitude / 2
  return Math.ceil(value / step) * step
}

function formatAxisValue(v: number): string {
  return Number.isInteger(v) ? String(v) : v.toFixed(1)
}

const HEALTH_HISTORY_RANGES = [
  { label: '1h', hours: 1 },
  { label: '24h', hours: 24 },
  { label: '7d', hours: 24 * 7 },
  { label: '30d', hours: 24 * 30 },
]

// HealthHistorySection shows current status (already covered by the tiles
// above) plus a graph of how it's trended over a selectable window, using
// whatever history the server has retained (up to 30 days — see
// healthHistoryRetention in server/store/store.go). Health reports buffered
// on the device during an outage (agent/healthbuffer.go) and replayed on
// reconnect are included here, not just live samples.
function HealthHistorySection({ deviceId }: { deviceId: string }) {
  const [hours, setHours] = useState(24)

  const { data: history = [], isLoading } = useQuery<HealthReport[]>({
    queryKey: ['health-history', deviceId, hours],
    queryFn: () => getHealthHistory(deviceId, hours),
    refetchInterval: 60_000,
  })

  const charts: { label: string; color: string; values: number[]; unit?: string; fixedMax?: number }[] = [
    { label: 'CPU', color: '#2563eb', values: history.map(h => h.CPUPercent), unit: '%', fixedMax: 100 },
    { label: 'Memory', color: '#7c3aed', values: history.map(h => h.MemoryPercent), unit: '%', fixedMax: 100 },
    { label: 'Active Calls', color: '#059669', values: history.map(h => h.ActiveCalls) },
    { label: 'Registered Endpoints', color: '#d97706', values: history.map(h => h.RegisteredEndpoints) },
    { label: 'Primary SIP Latency (ms)', color: '#dc2626', values: history.map(h => h.PrimaryLatencyMs) },
  ]

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>Health History</h3>
        <div style={{ display: 'flex', gap: 4 }}>
          {HEALTH_HISTORY_RANGES.map(r => (
            <button
              key={r.hours}
              onClick={() => setHours(r.hours)}
              style={{
                padding: '4px 10px',
                borderRadius: 6,
                border: '1px solid #d1d5db',
                background: hours === r.hours ? '#2563eb' : '#fff',
                color: hours === r.hours ? '#fff' : '#374151',
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>
      {isLoading && <p style={{ color: '#6b7280', fontSize: 13 }}>Loading...</p>}
      {!isLoading && history.length === 0 && (
        <p style={{ color: '#6b7280', fontSize: 13 }}>No health history yet for this period.</p>
      )}
      {!isLoading && history.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {charts.map(c => (
            <div key={c.label}>
              <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, marginBottom: 4 }}>{c.label}</div>
              <LineChart values={c.values} color={c.color} unit={c.unit} fixedMax={c.fixedMax} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function RingGroupsTab({ deviceId }: { deviceId: string }) {
  const qc = useQueryClient()
  const { data: rgs = [] } = useQuery<RingGroup[]>({
    queryKey: ['ring-groups', deviceId],
    queryFn: () => listRingGroups(deviceId),
  })

  const [editing, setEditing] = useState<Partial<RingGroup> | null>(null)
  // Kept separate from editing.MemberExtensions so the input isn't
  // reformatted on every keystroke — re-joining after each split() strips
  // the comma/space you just typed before you can type the next entry.
  const [memberExtensionsText, setMemberExtensionsText] = useState('')

  const [upsertError, setUpsertError] = useState<string | null>(null)
  const upsertMut = useMutation({
    mutationFn: (rg: Partial<RingGroup>) => upsertRingGroup(deviceId, rg),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ring-groups', deviceId] })
      setEditing(null)
      setUpsertError(null)
    },
    onError: (e: unknown) => {
      setUpsertError(axios.isAxiosError(e) ? (e.response?.data?.error ?? e.message) : String(e))
    },
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteRingGroup(deviceId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ring-groups', deviceId] }),
  })

  const [syncResult, setSyncResult] = useState<string | null>(null)
  const syncQueuesMut = useMutation({
    mutationFn: () => syncDeviceQueues(deviceId),
    onSuccess: (res) => {
      setSyncResult(`Synced ${res.queuesSynced} queue(s), skipped ${res.parksSkipped} park(s). Remember to Push Config to send this to the device.`)
      qc.invalidateQueries({ queryKey: ['ring-groups', deviceId] })
    },
    onError: (e) => setSyncResult('Error: ' + (e instanceof Error ? e.message : String(e))),
  })

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>Ring Groups</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            style={{ ...btnStyle, background: '#f3f4f6', color: '#374151' }}
            onClick={() => { setSyncResult(null); syncQueuesMut.mutate() }}
            disabled={syncQueuesMut.isPending}
          >
            {syncQueuesMut.isPending ? 'Syncing…' : 'Sync Call Queues'}
          </button>
          <button
            style={{ ...btnStyle, background: '#2563eb', color: '#fff' }}
            onClick={() => {
              setEditing({ Strategy: 0, TimeoutSeconds: 30, MemberExtensions: [] })
              setMemberExtensionsText('')
              setUpsertError(null)
            }}
          >
            + Add Ring Group
          </button>
        </div>
      </div>
      {syncResult && (
        <p style={{ fontSize: 12.5, color: syncResult.startsWith('Error') ? '#dc2626' : '#065f46', margin: '0 0 12px' }}>
          {syncResult}
        </p>
      )}

      {editing && (
        <div style={{ ...cardStyle, borderColor: '#2563eb' }}>
          <h4 style={{ margin: '0 0 12px' }}>{editing.ID ? 'Edit' : 'New'} Ring Group</h4>
          {upsertError && (
            <p style={{ fontSize: 13, color: '#dc2626', margin: '0 0 12px', background: '#fee2e2', borderRadius: 6, padding: '8px 12px' }}>
              {upsertError}
            </p>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Name</label>
              <input
                style={{ width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 14 }}
                value={editing.Name || ''}
                onChange={e => setEditing({ ...editing, Name: e.target.value })}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Extension</label>
              <input
                style={{ width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 14 }}
                value={editing.Extension || ''}
                onChange={e => setEditing({ ...editing, Extension: e.target.value })}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Strategy</label>
              <select
                style={{ width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 14 }}
                value={editing.Strategy || 0}
                onChange={e => setEditing({ ...editing, Strategy: Number(e.target.value) })}
              >
                {RING_STRATEGIES.map((s, i) => <option key={i} value={i}>{s}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Timeout (seconds)</label>
              <input
                type="number"
                style={{ width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 14 }}
                value={editing.TimeoutSeconds || 30}
                onChange={e => setEditing({ ...editing, TimeoutSeconds: Number(e.target.value) })}
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
                Member Extensions (comma-separated)
              </label>
              <input
                style={{ width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 14 }}
                value={memberExtensionsText}
                onChange={e => {
                  setMemberExtensionsText(e.target.value)
                  setEditing({
                    ...editing,
                    MemberExtensions: e.target.value.split(',').map(s => s.trim()).filter(Boolean),
                  })
                }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              style={{ ...btnStyle, background: '#2563eb', color: '#fff' }}
              onClick={() => upsertMut.mutate(editing)}
              disabled={upsertMut.isPending}
            >
              Save
            </button>
            <button
              style={{ ...btnStyle, background: '#f3f4f6', color: '#374151' }}
              onClick={() => setEditing(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Extension</th>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Strategy</th>
              <th style={thStyle}>Members</th>
              <th style={thStyle}>Timeout</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rgs.length === 0 && (
              <tr>
                <td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af', padding: 24 }}>
                  No ring groups configured.
                </td>
              </tr>
            )}
            {rgs.map(rg => (
              <tr key={rg.ID}>
                <td style={{ ...tdStyle, fontFamily: 'monospace', fontWeight: 600 }}>{rg.Extension}</td>
                <td style={tdStyle}>{rg.Name}</td>
                <td style={tdStyle}>{RING_STRATEGIES[rg.Strategy] ?? 'Unknown'}</td>
                <td style={{ ...tdStyle, fontSize: 12 }}>{(rg.MemberExtensions || []).join(', ')}</td>
                <td style={tdStyle}>{rg.TimeoutSeconds}s</td>
                <td style={tdStyle}>
                  <button
                    style={{ ...btnStyle, background: '#f3f4f6', color: '#374151', marginRight: 6 }}
                    onClick={() => {
                      setEditing(rg)
                      setMemberExtensionsText((rg.MemberExtensions || []).join(', '))
                      setUpsertError(null)
                    }}
                  >
                    Edit
                  </button>
                  <button
                    style={{ ...btnStyle, background: '#fee2e2', color: '#991b1b' }}
                    onClick={() => deleteMut.mutate(rg.ID)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// PatternBuilderMode drives the plain-language rule builder in the Cellular
// Outbound Routing form, generating the regex the store actually persists
// so operators don't need to know regex syntax for the common cases.
type PatternBuilderMode = 'startsWith' | 'exact' | 'length' | 'custom'

function buildPatternFromRule(mode: PatternBuilderMode, value: string, length: number): string {
  switch (mode) {
    case 'startsWith':
      return `^${value}`
    case 'exact':
      return `^${value}$`
    case 'length':
      return `^\\d{${length}}$`
    default:
      return ''
  }
}

function GSMPatternsTab({ device }: { device: Device }) {
  const deviceId = device.ID
  const qc = useQueryClient()
  const { data: patterns = [] } = useQuery<GSMPattern[]>({
    queryKey: ['gsm-patterns', deviceId],
    queryFn: () => listGSMPatterns(deviceId),
  })

  const [editing, setEditing] = useState<Partial<GSMPattern> | null>(null)
  const [builderMode, setBuilderMode] = useState<PatternBuilderMode>('startsWith')
  const [builderValue, setBuilderValue] = useState('')
  const [builderLength, setBuilderLength] = useState(10)

  const upsertMut = useMutation({
    mutationFn: (p: Partial<GSMPattern>) => upsertGSMPattern(deviceId, p),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gsm-patterns', deviceId] })
      setEditing(null)
    },
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteGSMPattern(deviceId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gsm-patterns', deviceId] }),
  })

  const { data: ringGroups = [] } = useQuery<RingGroup[]>({
    queryKey: ['ring-groups', deviceId],
    queryFn: () => listRingGroups(deviceId),
  })

  const [trunkURI, setTrunkURI] = useState(device.GsmTrunkURI ?? '')
  const [cellularInboundRingGroup, setCellularInboundRingGroup] = useState(device.CellularInboundRingGroup ?? '')
  const [trunkSaved, setTrunkSaved] = useState(false)
  const trunkSaveMut = useMutation({
    mutationFn: () => updateDeviceConfig(deviceId, {
      SipDomain: device.SipDomain ?? '',
      GsmTrunkURI: trunkURI,
      CellularInboundRingGroup: cellularInboundRingGroup,
      PrimarySipURI: device.PrimarySipURI ?? '',
    }),
    onSuccess: () => {
      setTrunkSaved(true)
      qc.invalidateQueries({ queryKey: ['device', deviceId] })
      setTimeout(() => setTrunkSaved(false), 3000)
    },
  })
  const trunkInputSt: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box',
    padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14,
  }

  return (
    <div>
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 20px', fontSize: 16 }}>Cellular Trunk URI</h3>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
          Cellular Trunk URI
        </label>
        <input
          style={{ ...trunkInputSt, marginBottom: 6 }}
          placeholder="sip:cellular-gateway.local:5060"
          value={trunkURI}
          onChange={e => setTrunkURI(e.target.value)}
        />
        <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 20px' }}>
          The cellular trunk URI is the SIP endpoint used for calls matching the routing patterns below.
        </p>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
          Cellular Inbound Routing
        </label>
        <select
          style={{ ...trunkInputSt, marginBottom: 6 }}
          value={cellularInboundRingGroup}
          onChange={e => setCellularInboundRingGroup(e.target.value)}
        >
          <option value="">— no inbound routing —</option>
          {ringGroups.map(rg => (
            <option key={rg.ID} value={rg.Extension}>{rg.Extension} — {rg.Name}</option>
          ))}
        </select>
        <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 20px' }}>
          Any inbound call arriving from the cellular gateway is routed to this ring group, regardless of the dialed number.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            style={{ ...btnStyle, background: '#2563eb', color: '#fff' }}
            onClick={() => trunkSaveMut.mutate()}
            disabled={trunkSaveMut.isPending}
          >
            {trunkSaveMut.isPending ? 'Saving…' : 'Save'}
          </button>
          {trunkSaved && <span style={{ fontSize: 13, color: '#065f46' }}>Saved.</span>}
          {trunkSaveMut.isError && (
            <span style={{ fontSize: 13, color: '#dc2626' }}>Save failed.</span>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>Cellular Outbound Routing</h3>
        <button
          style={{ ...btnStyle, background: '#2563eb', color: '#fff' }}
          onClick={() => {
            setEditing({ Enabled: true, Priority: 10 })
            setBuilderMode('startsWith')
            setBuilderValue('')
            setBuilderLength(10)
          }}
        >
          + Add Pattern
        </button>
      </div>

      {editing && (
        <div style={{ ...cardStyle, borderColor: '#2563eb' }}>
          <h4 style={{ margin: '0 0 12px' }}>{editing.ID ? 'Edit' : 'New'} Cellular Pattern</h4>

          <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, padding: 12, marginBottom: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
              Build a rule (no regex needed)
            </label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <select
                style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 14 }}
                value={builderMode}
                onChange={e => setBuilderMode(e.target.value as PatternBuilderMode)}
              >
                <option value="startsWith">Number starts with…</option>
                <option value="exact">Number is exactly…</option>
                <option value="length">Number is exactly this many digits…</option>
                <option value="custom">Custom (regex)</option>
              </select>
              {(builderMode === 'startsWith' || builderMode === 'exact') && (
                <input
                  style={{ width: 160, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 14 }}
                  placeholder="e.g. 1900"
                  value={builderValue}
                  onChange={e => setBuilderValue(e.target.value.replace(/[^0-9]/g, ''))}
                />
              )}
              {builderMode === 'length' && (
                <input
                  type="number"
                  style={{ width: 100, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 14 }}
                  min={1}
                  value={builderLength}
                  onChange={e => setBuilderLength(Number(e.target.value))}
                />
              )}
              {builderMode !== 'custom' && (
                <button
                  type="button"
                  style={{ ...btnStyle, background: '#2563eb', color: '#fff' }}
                  disabled={(builderMode === 'startsWith' || builderMode === 'exact') && !builderValue}
                  onClick={() => setEditing({ ...editing, Pattern: buildPatternFromRule(builderMode, builderValue, builderLength) })}
                >
                  Use this rule
                </button>
              )}
            </div>
            {builderMode !== 'custom' && (
              <p style={{ fontSize: 12, color: '#6b7280', margin: '8px 0 0' }}>
                Generates: <code>{buildPatternFromRule(builderMode, builderValue || '…', builderLength)}</code>
              </p>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Pattern (regex)</label>
              <input
                style={{ width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 14, fontFamily: 'monospace' }}
                value={editing.Pattern || ''}
                onChange={e => setEditing({ ...editing, Pattern: e.target.value })}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Description</label>
              <input
                style={{ width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 14 }}
                value={editing.Description || ''}
                onChange={e => setEditing({ ...editing, Description: e.target.value })}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Priority</label>
              <input
                type="number"
                style={{ width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 14 }}
                value={editing.Priority || 0}
                onChange={e => setEditing({ ...editing, Priority: Number(e.target.value) })}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 20 }}>
              <input
                type="checkbox"
                id="enabled"
                checked={editing.Enabled ?? true}
                onChange={e => setEditing({ ...editing, Enabled: e.target.checked })}
              />
              <label htmlFor="enabled" style={{ fontSize: 14, fontWeight: 500 }}>Enabled</label>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              style={{ ...btnStyle, background: '#2563eb', color: '#fff' }}
              onClick={() => upsertMut.mutate(editing)}
              disabled={upsertMut.isPending}
            >
              Save
            </button>
            <button
              style={{ ...btnStyle, background: '#f3f4f6', color: '#374151' }}
              onClick={() => setEditing(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Pattern</th>
              <th style={thStyle}>Description</th>
              <th style={thStyle}>Priority</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {patterns.length === 0 && (
              <tr>
                <td colSpan={5} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af', padding: 24 }}>
                  No cellular patterns configured.
                </td>
              </tr>
            )}
            {patterns.map(p => (
              <tr key={p.ID}>
                <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 12 }}>{p.Pattern}</td>
                <td style={tdStyle}>{p.Description}</td>
                <td style={tdStyle}>{p.Priority}</td>
                <td style={tdStyle}>
                  <span style={{
                    padding: '2px 8px',
                    borderRadius: 9999,
                    fontSize: 12,
                    fontWeight: 600,
                    background: p.Enabled ? '#d1fae5' : '#f3f4f6',
                    color: p.Enabled ? '#065f46' : '#6b7280',
                  }}>
                    {p.Enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </td>
                <td style={tdStyle}>
                  <button
                    style={{ ...btnStyle, background: '#f3f4f6', color: '#374151', marginRight: 6 }}
                    onClick={() => {
                      setEditing(p)
                      setBuilderMode('custom')
                      setBuilderValue('')
                      setBuilderLength(10)
                    }}
                  >
                    Edit
                  </button>
                  <button
                    style={{ ...btnStyle, background: '#fee2e2', color: '#991b1b' }}
                    onClick={() => deleteMut.mutate(p.ID)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function UpdatesTab({ device }: { device: Device }) {
  const deviceId = device.ID
  const { data: releases = [] } = useQuery<Release[]>({
    queryKey: ['releases'],
    queryFn: listReleases,
  })

  const [pushType, setPushType] = useState<UpdateType>('agent')
  const [selectedRelease, setSelectedRelease] = useState('')
  const [packageSpec, setPackageSpec] = useState('')
  const [lastResult, setLastResult] = useState<string | null>(null)

  const pushMut = useMutation({
    mutationFn: () => {
      const rel = releases.find(r => r.Version === selectedRelease)
      return pushUpdate(deviceId, pushType, {
        releaseVersion: rel?.Version,
        downloadUrl: rel?.DownloadURL,
        sha256: rel?.SHA256,
        packageSpec: pushType === 'package' ? packageSpec : undefined,
      })
    },
    onSuccess: (res) => setLastResult(res.online ? 'Queued successfully.' : 'Device offline — will deliver on reconnect.'),
    onError: (e) => setLastResult('Error: ' + (e instanceof Error ? e.message : String(e))),
  })

  const [rebootResult, setRebootResult] = useState<string | null>(null)
  const rebootMut = useMutation({
    mutationFn: () => pushUpdate(deviceId, 'reboot'),
    onSuccess: (res) => setRebootResult(res.online ? 'Reboot queued.' : 'Device offline — will reboot on reconnect.'),
    onError: (e) => setRebootResult('Error: ' + (e instanceof Error ? e.message : String(e))),
  })

  const inputSt: React.CSSProperties = {
    padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 5,
    fontSize: 14, width: '100%', boxSizing: 'border-box',
  }

  return (
    <div>

      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Reboot Device</h3>
        <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 12px' }}>
          Reboots the device immediately. Calls in progress will be dropped.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            style={{ ...btnStyle, background: '#dc2626', color: '#fff' }}
            onClick={() => {
              if (!window.confirm('Reboot this device now? Calls in progress will be dropped.')) return
              setRebootResult(null)
              rebootMut.mutate()
            }}
            disabled={rebootMut.isPending}
          >
            {rebootMut.isPending ? 'Rebooting…' : 'Reboot Device'}
          </button>
          {rebootResult && (
            <span style={{ fontSize: 13, color: rebootResult.startsWith('Error') ? '#dc2626' : '#065f46' }}>
              {rebootResult}
            </span>
          )}
        </div>
      </div>

      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 16px', fontSize: 16 }}>Push Update</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Update Type</label>
            <select style={inputSt} value={pushType} onChange={e => { setPushType(e.target.value as UpdateType); setSelectedRelease(''); setPackageSpec('') }}>
              <option value="agent">Agent Binary</option>
              <option value="package">APT Package</option>
              <option value="os">OS Upgrade (dist-upgrade)</option>
            </select>
          </div>
          {pushType === 'agent' && (
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Release</label>
              <select style={inputSt} value={selectedRelease} onChange={e => setSelectedRelease(e.target.value)}>
                <option value="">— select release —</option>
                {releases.map(r => (
                  <option key={r.Version} value={r.Version}>
                    {r.Version}{r.IsLatest ? ' (latest)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
          {pushType === 'package' && (
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
                Package Spec
              </label>
              <input
                style={inputSt}
                placeholder="e.g. kamailio=5.8.1 or kamailio"
                value={packageSpec}
                onChange={e => setPackageSpec(e.target.value)}
              />
            </div>
          )}
        </div>
        {pushType === 'os' && (
          <p style={{ fontSize: 13, color: '#92400e', background: '#fef3c7', borderRadius: 6, padding: '8px 12px', marginBottom: 12 }}>
            This will run <code>apt-get dist-upgrade</code> on the device. The device may reboot if required.
          </p>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            style={{ ...btnStyle, background: '#dc2626', color: '#fff' }}
            onClick={() => { setLastResult(null); pushMut.mutate() }}
            disabled={pushMut.isPending || (pushType === 'agent' && !selectedRelease) || (pushType === 'package' && !packageSpec.trim())}
          >
            {pushMut.isPending ? 'Sending…' : 'Push Update'}
          </button>
          {lastResult && (
            <span style={{ fontSize: 13, color: lastResult.startsWith('Error') ? '#dc2626' : '#065f46' }}>
              {lastResult}
            </span>
          )}
        </div>
      </div>

      <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Available Releases</h3>
      <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Version</th>
              <th style={thStyle}>Published</th>
              <th style={thStyle}>Notes</th>
              <th style={thStyle}>Status</th>
            </tr>
          </thead>
          <tbody>
            {releases.length === 0 && (
              <tr>
                <td colSpan={4} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af', padding: 24 }}>
                  No releases published.
                </td>
              </tr>
            )}
            {releases.map(r => {
              const installed = r.Version === device.CurrentVersion
              return (
                <tr key={r.Version} style={installed ? { background: '#f0fdf4' } : undefined}>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontWeight: 600 }}>{r.Version}</td>
                  <td style={tdStyle}>{r.PublishedAt ? new Date(r.PublishedAt * 1000).toLocaleDateString() : '—'}</td>
                  <td style={{ ...tdStyle, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.Notes || '—'}
                  </td>
                  <td style={{ ...tdStyle, display: 'flex', gap: 6 }}>
                    {r.IsLatest && (
                      <span style={{ padding: '2px 8px', borderRadius: 9999, fontSize: 12, fontWeight: 600, background: '#dbeafe', color: '#1e40af' }}>
                        Latest
                      </span>
                    )}
                    {installed && (
                      <span style={{ padding: '2px 8px', borderRadius: 9999, fontSize: 12, fontWeight: 600, background: '#d1fae5', color: '#065f46' }}>
                        Installed
                      </span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {!releases.some(r => r.Version === device.CurrentVersion) && (
        <p style={{ fontSize: 12, color: '#6b7280', margin: '8px 0 0' }}>
          Currently installed: <span style={{ fontFamily: 'monospace' }}>{device.CurrentVersion || 'unknown'}</span> (not in the release list above)
        </p>
      )}
    </div>
  )
}

// ResolversInput keeps the raw comma-separated text in local state and only
// parses it into a string[] on each change — deriving the input's `value`
// directly from an already-parsed array (via .join(', ')) re-strips a
// trailing comma the instant it's typed, since split+filter(Boolean) drops
// the empty segment a trailing comma creates before the next render. Give
// this a `key` tied to which item is being edited so it re-seeds instead of
// carrying stale text across edits.
function ResolversInput({ value, onChange, style, placeholder }: {
  value: string[]
  onChange: (v: string[]) => void
  style: React.CSSProperties
  placeholder?: string
}) {
  const [text, setText] = useState(value.join(', '))
  return (
    <input
      style={style}
      placeholder={placeholder}
      value={text}
      onChange={e => {
        setText(e.target.value)
        onChange(e.target.value.split(',').map(s => s.trim()).filter(Boolean))
      }}
    />
  )
}

function NetworkTab({ deviceId }: { deviceId: string }) {
  const qc = useQueryClient()
  const { data: segments = [] } = useQuery<NetworkSegment[]>({
    queryKey: ['network-segments', deviceId],
    queryFn: () => listNetworkSegments(deviceId),
  })
  // Same queryKey as the parent DeviceDetail's device fetch, so this hits
  // the existing cache instead of firing a redundant request.
  const { data: device } = useQuery<Device>({
    queryKey: ['device', deviceId],
    queryFn: () => getDevice(deviceId),
  })
  const knownInterfaces = device?.Interfaces ?? []

  const [editing, setEditing] = useState<Partial<NetworkSegment> | null>(null)

  const upsertMut = useMutation({
    mutationFn: (seg: Partial<NetworkSegment>) => upsertNetworkSegment(deviceId, seg),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['network-segments', deviceId] })
      setEditing(null)
    },
  })
  const deleteMut = useMutation({
    mutationFn: (name: string) => deleteNetworkSegment(deviceId, name),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['network-segments', deviceId] }),
  })

  const segmentLimit = maxNetworkSegments(device?.TotalMemoryMB ?? 0)
  const atMax = segments.length >= segmentLimit
  const relayActive = segments.length === 2
  const inputSt: React.CSSProperties = {
    width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 14,
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h3 style={{ margin: 0 }}>Phone Segments</h3>
        <button
          style={{ ...btnStyle, background: atMax ? '#93c5fd' : '#2563eb', color: '#fff', cursor: atMax ? 'not-allowed' : 'pointer' }}
          disabled={atMax}
          onClick={() => setEditing({ Interface: 'eth0', Ordinal: segments.length })}
        >
          + Add Segment
        </button>
      </div>
      <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 16px', maxWidth: '70ch' }}>
        Leave empty for a flat LAN (phones on one network, media flows phone-to-phone).
        {segmentLimit >= 2
          ? <> Add two segments — e.g. <b>admin</b> and <b>guest</b> — to place the appliance on both
              VLANs; cross-segment calls are then relayed automatically by the device. Maximum two.</>
          : <> This device's hardware tier supports one segment — a single tagged VLAN on its one
              interface is fine, but a second segment (which triggers media relay between segments)
              needs the dual-NIC tier.</>}
      </p>

      <div style={{
        ...cardStyle, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10,
        background: relayActive ? '#e6f4ec' : '#f9fafb', borderColor: relayActive ? '#37a878' : '#e5e7eb',
      }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: relayActive ? '#1c855a' : '#6b7280' }}>
          {relayActive ? '● Media relay active' : '○ Flat LAN — no media relay'}
        </span>
        <span style={{ fontSize: 12.5, color: '#6b7280' }}>
          {relayActive
            ? 'Cross-segment calls bridge through the device; same-segment media stays direct.'
            : `${segments.length} of ${segmentLimit} segment${segmentLimit === 1 ? '' : 's'} configured.`}
        </span>
      </div>

      {editing && (
        <div style={{ ...cardStyle, borderColor: '#2563eb' }}>
          <h4 style={{ margin: '0 0 12px' }}>{editing.Name ? 'Edit' : 'New'} Segment</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Name</label>
              <input style={inputSt} placeholder="admin" value={editing.Name || ''}
                onChange={e => setEditing({ ...editing, Name: e.target.value })} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>VLAN ID (0 = untagged / physical NIC)</label>
              <input type="number" style={inputSt} value={editing.VlanID ?? 0}
                onChange={e => setEditing({ ...editing, VlanID: Number(e.target.value) })} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Base interface</label>
              {knownInterfaces.length > 0 ? (
                <select style={inputSt} value={editing.Interface || ''}
                  onChange={e => setEditing({ ...editing, Interface: e.target.value })}>
                  <option value="">— select interface —</option>
                  {knownInterfaces.map(name => <option key={name} value={name}>{name}</option>)}
                </select>
              ) : (
                <input style={inputSt} placeholder="eth0" value={editing.Interface || ''}
                  onChange={e => setEditing({ ...editing, Interface: e.target.value })} />
              )}
              {knownInterfaces.length === 0 && (
                <p style={{ fontSize: 11.5, color: '#9ca3af', margin: '4px 0 0' }}>
                  Device hasn't reported its interfaces yet (needs a recent agent version and at least one connection) — type the name manually for now.
                </p>
              )}
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Device address (CIDR)</label>
              <input style={{ ...inputSt, fontFamily: 'monospace' }} placeholder="10.10.0.2/24" value={editing.AddressCIDR || ''}
                onChange={e => setEditing({ ...editing, AddressCIDR: e.target.value })} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Gateway (optional)</label>
              <input style={{ ...inputSt, fontFamily: 'monospace' }} placeholder="10.10.0.1" value={editing.Gateway || ''}
                onChange={e => setEditing({ ...editing, Gateway: e.target.value })} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
                DNS failover resolvers override (optional, comma-separated)
              </label>
              <ResolversInput
                key={editing.Name ?? 'new'}
                style={inputSt}
                placeholder="blank = use the device-wide default"
                value={editing.DNSUpstreamResolvers ?? []}
                onChange={v => setEditing({ ...editing, DNSUpstreamResolvers: v })}
              />
              <p style={{ fontSize: 11.5, color: '#9ca3af', margin: '4px 0 0' }}>
                Only matters if DNS Failover is enabled in Settings — lets this segment forward
                to a different resolver than the device default (e.g. a guest VLAN whose
                firewall only permits a specific internal resolver).
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={{ ...btnStyle, background: '#2563eb', color: '#fff' }}
              onClick={() => upsertMut.mutate(editing)}
              disabled={upsertMut.isPending || !editing.Name || !editing.AddressCIDR}>
              Save
            </button>
            <button style={{ ...btnStyle, background: '#f3f4f6', color: '#374151' }} onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>VLAN</th>
              <th style={thStyle}>Interface</th>
              <th style={thStyle}>Address</th>
              <th style={thStyle}>Gateway</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {segments.length === 0 && (
              <tr>
                <td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af', padding: 24 }}>
                  No segments — device operates as a flat LAN.
                </td>
              </tr>
            )}
            {segments.map(s => (
              <tr key={s.Name}>
                <td style={{ ...tdStyle, fontWeight: 600 }}>{s.Name}</td>
                <td style={tdStyle}>{s.VlanID > 0 ? s.VlanID : 'untagged'}</td>
                <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 12 }}>{s.Interface}</td>
                <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 12 }}>{s.AddressCIDR}</td>
                <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 12 }}>{s.Gateway || '—'}</td>
                <td style={tdStyle}>
                  <button style={{ ...btnStyle, background: '#f3f4f6', color: '#374151', marginRight: 6 }}
                    onClick={() => setEditing(s)}>Edit</button>
                  <button style={{ ...btnStyle, background: '#fee2e2', color: '#991b1b' }}
                    onClick={() => deleteMut.mutate(s.Name)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function DeviceIdentityCard({ device, inputSt }: { device: Device; inputSt: React.CSSProperties }) {
  const { user } = useAuth()
  const qc = useQueryClient()
  const [name, setName] = useState(device.Name ?? '')
  const [site, setSite] = useState(device.Site ?? '')
  const [orgID, setOrgID] = useState<number | ''>(device.OrgID ?? '')
  const [netsapiensDomain, setNetsapiensDomain] = useState(device.NetsapiensDomain ?? '')
  const [saved, setSaved] = useState(false)

  // Needed for every device (not just super users) to look up the device's
  // own org and its platform/reseller link, which scopes the live domain
  // picker below — listOrganizations already only returns orgs the caller
  // can see, so this is safe regardless of permission level.
  const { data: orgs = [] } = useQuery<Organization[]>({ queryKey: ['organizations'], queryFn: listOrganizations })
  const deviceOrg = orgs.find(o => o.ID === device.OrgID)

  const { data: domains = [], isFetching: fetchingDomains } = useQuery<NetsapiensDomain[]>({
    queryKey: ['platform-domains', deviceOrg?.PlatformID, deviceOrg?.ResellerName],
    queryFn: () => listPlatformDomains(deviceOrg!.PlatformID!, deviceOrg!.ResellerName),
    enabled: !!deviceOrg?.PlatformID && !!deviceOrg?.ResellerName,
  })

  const saveMut = useMutation({
    mutationFn: () => {
      const patch: Partial<Device> = { Name: name, Site: site }
      if (user?.IsSuperUser && orgID !== '' && orgID !== device.OrgID) {
        patch.OrgID = orgID
      }
      if (netsapiensDomain !== (device.NetsapiensDomain ?? '') && deviceOrg?.PlatformID) {
        patch.PlatformID = deviceOrg.PlatformID
        patch.NetsapiensDomain = netsapiensDomain
      }
      return updateDevice(device.ID, patch)
    },
    onSuccess: () => {
      setSaved(true)
      qc.invalidateQueries({ queryKey: ['device', device.ID] })
      qc.invalidateQueries({ queryKey: ['devices'] })
      setTimeout(() => setSaved(false), 3000)
    },
  })

  return (
    <div style={cardStyle}>
      <h3 style={{ margin: '0 0 20px', fontSize: 16 }}>Device Identity</h3>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Name</label>
      <input
        style={{ ...inputSt, marginBottom: 16 }}
        placeholder={device.ID}
        value={name}
        onChange={e => setName(e.target.value)}
      />
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Site</label>
      <input
        style={{ ...inputSt, marginBottom: 16 }}
        placeholder="e.g. Main Office"
        value={site}
        onChange={e => setSite(e.target.value)}
      />
      {user?.IsSuperUser && (
        <>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Organization</label>
          <select
            style={{ ...inputSt, marginBottom: 16 }}
            value={orgID}
            onChange={e => setOrgID(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <option value="">— unassigned —</option>
            {orgs.map(o => <option key={o.ID} value={o.ID}>{o.Name}</option>)}
          </select>
        </>
      )}
      {deviceOrg?.PlatformID && deviceOrg?.ResellerName ? (
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Platform Domain</label>
          <SearchableSelect
            options={domains.map(d => ({ value: d.Domain, label: d.Domain }))}
            value={netsapiensDomain}
            onChange={setNetsapiensDomain}
            placeholder={fetchingDomains ? 'Loading domains…' : 'Type to search domains…'}
            disabled={fetchingDomains}
            emptyMessage="No domains match"
          />
        </div>
      ) : (
        <p style={{ fontSize: 12.5, color: '#9ca3af', margin: '0 0 16px' }}>
          This device's organization has no platform linked — an admin can set one on the
          Organizations page to enable syncing call queues from a customer domain.
        </p>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          style={{ ...btnStyle, background: '#2563eb', color: '#fff' }}
          onClick={() => saveMut.mutate()}
          disabled={saveMut.isPending}
        >
          {saveMut.isPending ? 'Saving…' : 'Save'}
        </button>
        {saved && <span style={{ fontSize: 13, color: '#065f46' }}>Saved.</span>}
        {saveMut.isError && <span style={{ fontSize: 13, color: '#dc2626' }}>Save failed.</span>}
      </div>
    </div>
  )
}

function SettingsTab({ device }: { device: Device }) {
  const inputSt: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box',
    padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14,
  }

  return (
    <div style={{ maxWidth: 540 }}>
      <DeviceIdentityCard device={device} inputSt={inputSt} />

      <PerformanceCard device={device} />
      <DNSFailoverCard device={device} />
      <PrimaryServiceCard device={device} />
      <SipTlsCard device={device} />
      <SrtpBridgingCard device={device} />
      <SipAuthCard device={device} />
      <DisableDeviceCard device={device} />
    </div>
  )
}

// PrimaryServiceCard configures the customer's primary cloud UCaaS/SIP
// service for the agent's independent OPTIONS-ping reachability check
// (see the "Primary Service" tile and latency chart on the Overview tab)
// — entirely separate from this device's own failover behavior. Uses the
// same updateDeviceConfig endpoint the Cellular tab's trunk-URI card
// does, so it must pass through the other config fields unchanged rather
// than clobbering them.
function PrimaryServiceCard({ device }: { device: Device }) {
  const qc = useQueryClient()
  const [uri, setUri] = useState(device.PrimarySipURI ?? '')
  const [saved, setSaved] = useState(false)

  const saveMut = useMutation({
    mutationFn: () => updateDeviceConfig(device.ID, {
      SipDomain: device.SipDomain ?? '',
      GsmTrunkURI: device.GsmTrunkURI ?? '',
      CellularInboundRingGroup: device.CellularInboundRingGroup ?? '',
      PrimarySipURI: uri.trim(),
    }),
    onSuccess: () => {
      setSaved(true)
      qc.invalidateQueries({ queryKey: ['device', device.ID] })
      setTimeout(() => setSaved(false), 3000)
    },
  })

  const inputSt: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box',
    padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14,
  }

  return (
    <div style={cardStyle}>
      <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>Primary Service Monitoring</h3>
      <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 20px' }}>
        Optional. The agent pings this address with a SIP OPTIONS request every 30 seconds
        to independently track whether the customer's primary cloud UCaaS/SIP service is
        reachable — not this device's own health, and not anything about failover behavior.
        Leave blank to disable.
      </p>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
        Primary SIP Service
      </label>
      <input
        style={{ ...inputSt, marginBottom: 14 }}
        placeholder="sip:core1-atl.example.com:5060"
        value={uri}
        onChange={e => setUri(e.target.value)}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          style={{ ...btnStyle, background: '#2563eb', color: '#fff' }}
          onClick={() => saveMut.mutate()}
          disabled={saveMut.isPending}
        >
          {saveMut.isPending ? 'Saving…' : 'Save'}
        </button>
        {saved && <span style={{ fontSize: 12.5, color: '#065f46' }}>Saved.</span>}
      </div>
    </div>
  )
}

function SipTlsCard({ device }: { device: Device }) {
  const qc = useQueryClient()
  const [result, setResult] = useState<string | null>(null)

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['device', device.ID] })
    qc.invalidateQueries({ queryKey: ['devices'] })
  }

  const toggleMut = useMutation({
    mutationFn: () => (device.SipTlsEnabled ? disableDeviceSipTLS(device.ID) : enableDeviceSipTLS(device.ID)),
    onSuccess: (res) => {
      setResult(res.online ? 'Applied immediately.' : 'Device offline — will apply on reconnect.')
      invalidate()
    },
    onError: (e) => setResult('Error: ' + (e instanceof Error ? e.message : String(e))),
  })

  const regenerateMut = useMutation({
    mutationFn: () => regenerateDeviceSipTLSCert(device.ID),
    onSuccess: (res) => {
      setResult('Certificate regenerated. ' + (res.online ? 'Applied immediately.' : 'Device offline — will apply on reconnect.'))
      invalidate()
    },
    onError: (e) => setResult('Error: ' + (e instanceof Error ? e.message : String(e))),
  })

  return (
    <div style={cardStyle}>
      <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>SIP TLS</h3>
      <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 8px' }}>
        Adds an encrypted TLS listener alongside the existing plaintext SIP listeners
        (it doesn't replace them — phones can migrate at their own pace). The certificate
        is signed by this deployment's own CA; phones need that CA imported as a trusted
        root to validate it.
      </p>
      <p style={{ fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 4, padding: '6px 10px', margin: '0 0 12px' }}>
        New, gradually-rolled-out functionality — verify on real hardware before relying on it.
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
        <button
          style={{ ...btnStyle, background: device.SipTlsEnabled ? '#dc2626' : '#2563eb', color: '#fff' }}
          onClick={() => {
            setResult(null)
            toggleMut.mutate()
          }}
          disabled={toggleMut.isPending}
        >
          {toggleMut.isPending ? 'Applying…' : device.SipTlsEnabled ? 'Disable SIP TLS' : 'Enable SIP TLS'}
        </button>
        {device.SipTlsEnabled && (
          <button
            style={{ ...btnStyle, background: '#f3f4f6', color: '#374151' }}
            onClick={() => {
              if (!window.confirm('Regenerate the SIP TLS certificate? Do this after changing the SIP domain or network segments.')) return
              setResult(null)
              regenerateMut.mutate()
            }}
            disabled={regenerateMut.isPending}
          >
            {regenerateMut.isPending ? 'Regenerating…' : 'Regenerate Certificate'}
          </button>
        )}
        <a href={caCertDownloadURL} style={{ fontSize: 13, color: '#2563eb' }}>
          Download CA Certificate (for phone provisioning)
        </a>
      </div>
      {result && (
        <span style={{ fontSize: 13, color: result.startsWith('Error') ? '#dc2626' : '#065f46' }}>
          {result}
        </span>
      )}
    </div>
  )
}

function SrtpBridgingCard({ device }: { device: Device }) {
  const qc = useQueryClient()
  const [result, setResult] = useState<string | null>(null)

  // Same client-side mirror of the server-side hardware-tier threshold
  // already used for network segment limits (client.ts's maxNetworkSegments
  // doc comment) — the base tier isn't sized for rtpengine on every call.
  const supported = maxNetworkSegments(device.TotalMemoryMB) >= 2

  const toggleMut = useMutation({
    mutationFn: () => (device.SrtpBridgingEnabled ? disableDeviceSrtpBridging(device.ID) : enableDeviceSrtpBridging(device.ID)),
    onSuccess: (res) => {
      setResult(res.online ? 'Applied immediately.' : 'Device offline — will apply on reconnect.')
      qc.invalidateQueries({ queryKey: ['device', device.ID] })
      qc.invalidateQueries({ queryKey: ['devices'] })
    },
    onError: (e) => setResult('Error: ' + (e instanceof Error ? e.message : String(e))),
  })

  return (
    <div style={cardStyle}>
      <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>RTP/SRTP Media Bridging</h3>
      <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 12px' }}>
        Routes every call — not just cross-segment ones — through the media relay so it
        can bridge calls between an RTP-only phone and an SRTP-only phone. Requires this
        device's high-RAM/dual-NIC hardware tier.
      </p>
      {!supported && (
        <p style={{ fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 4, padding: '6px 10px', margin: '0 0 12px' }}>
          This device's hardware tier does not support RTP/SRTP media bridging.
        </p>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          style={{ ...btnStyle, background: device.SrtpBridgingEnabled ? '#dc2626' : '#2563eb', color: '#fff' }}
          onClick={() => {
            setResult(null)
            toggleMut.mutate()
          }}
          disabled={toggleMut.isPending || (!supported && !device.SrtpBridgingEnabled)}
        >
          {toggleMut.isPending ? 'Applying…' : device.SrtpBridgingEnabled ? 'Disable Media Bridging' : 'Enable Media Bridging'}
        </button>
        {result && (
          <span style={{ fontSize: 13, color: result.startsWith('Error') ? '#dc2626' : '#065f46' }}>
            {result}
          </span>
        )}
      </div>
    </div>
  )
}

function SipAuthCard({ device }: { device: Device }) {
  const qc = useQueryClient()
  const [result, setResult] = useState<string | null>(null)
  const [lastSynced, setLastSynced] = useState<number | null>(null)

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['device', device.ID] })
    qc.invalidateQueries({ queryKey: ['devices'] })
  }

  const syncMut = useMutation({
    mutationFn: () => syncDeviceCredentials(device.ID),
    onSuccess: (res) => {
      setLastSynced(res.credentialsSynced)
      setResult(`Synced ${res.credentialsSynced} credential(s).`)
    },
    onError: (e) => setResult('Error: ' + (e instanceof Error ? e.message : String(e))),
  })

  const toggleMut = useMutation({
    mutationFn: () => (device.SipAuthEnabled ? disableDeviceSipAuth(device.ID) : enableDeviceSipAuth(device.ID)),
    onSuccess: (res) => {
      setResult(res.online ? 'Applied immediately.' : 'Device offline — will apply on reconnect.')
      invalidate()
    },
    onError: (e) => setResult('Error: ' + (e instanceof Error ? e.message : String(e))),
  })

  return (
    <div style={cardStyle}>
      <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>SIP Digest Authentication</h3>
      <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 12px' }}>
        Requires phones to authenticate on REGISTER. Credentials are fetched from the
        linked platform and stored as a digest hash — the plaintext password is never
        saved. Off by default; sync credentials before enabling, or registrations will
        fail for any identity without a synced credential.
      </p>
      {device.SipAuthEnabled && lastSynced === null && (
        <p style={{ fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 4, padding: '6px 10px', margin: '0 0 12px' }}>
          No credentials synced yet this session — sync before enabling, or registrations may fail.
        </p>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button
          style={{ ...btnStyle, background: '#f3f4f6', color: '#374151' }}
          onClick={() => {
            setResult(null)
            syncMut.mutate()
          }}
          disabled={syncMut.isPending}
        >
          {syncMut.isPending ? 'Syncing…' : 'Sync Credentials from Platform'}
        </button>
        <button
          style={{ ...btnStyle, background: device.SipAuthEnabled ? '#dc2626' : '#2563eb', color: '#fff' }}
          onClick={() => {
            setResult(null)
            toggleMut.mutate()
          }}
          disabled={toggleMut.isPending}
        >
          {toggleMut.isPending ? 'Applying…' : device.SipAuthEnabled ? 'Disable Digest Auth' : 'Enable Digest Auth'}
        </button>
        {result && (
          <span style={{ fontSize: 13, color: result.startsWith('Error') ? '#dc2626' : '#065f46' }}>
            {result}
          </span>
        )}
      </div>
    </div>
  )
}

function DisableDeviceCard({ device }: { device: Device }) {
  const qc = useQueryClient()
  const [result, setResult] = useState<string | null>(null)

  const disableMut = useMutation({
    mutationFn: () => (device.Disabled ? enableDevice(device.ID) : disableDevice(device.ID)),
    onSuccess: (res) => {
      setResult(res.online ? 'Applied immediately.' : 'Device offline — will apply on reconnect.')
      qc.invalidateQueries({ queryKey: ['device', device.ID] })
      qc.invalidateQueries({ queryKey: ['devices'] })
    },
    onError: (e) => setResult('Error: ' + (e instanceof Error ? e.message : String(e))),
  })

  return (
    <div style={cardStyle}>
      <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>{device.Disabled ? 'Enable Device' : 'Disable Device'}</h3>
      <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 12px' }}>
        {device.Disabled
          ? `Disabled${device.DisabledReason ? ` (${device.DisabledReason})` : ''}${device.DisabledAt ? ' since ' + new Date(device.DisabledAt).toLocaleString() : ''}. Re-enabling resumes normal operation on the next config push.`
          : "Stops the SIP Proxy and Media Relay on this device — it stops acting as a live SIP failover target until re-enabled. DNS failover keeps running."}
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          style={{ ...btnStyle, background: device.Disabled ? '#2563eb' : '#dc2626', color: '#fff' }}
          onClick={() => {
            const msg = device.Disabled
              ? 'Re-enable this device? It will resume the SIP Proxy and Media Relay on its next config push.'
              : 'Disable this device? The SIP Proxy and Media Relay will be stopped (DNS failover keeps running).'
            if (!window.confirm(msg)) return
            setResult(null)
            disableMut.mutate()
          }}
          disabled={disableMut.isPending}
        >
          {disableMut.isPending ? 'Applying…' : device.Disabled ? 'Enable Device' : 'Disable Device'}
        </button>
        {result && (
          <span style={{ fontSize: 13, color: result.startsWith('Error') ? '#dc2626' : '#065f46' }}>
            {result}
          </span>
        )}
      </div>
    </div>
  )
}

function DNSFailoverCard({ device }: { device: Device }) {
  const qc = useQueryClient()
  const [fqdn, setFqdn] = useState(device.DNSFailoverFQDN ?? '')
  const [resolversText, setResolversText] = useState((device.DNSUpstreamResolvers ?? []).join(', '))
  const [saved, setSaved] = useState(false)

  // Resolve the platform this device is linked to (if any), so we can hint
  // what it'll inherit when the fields above are left blank — mirrors the
  // server-side inheritance in GetDeviceConfig.
  const { data: platforms = [] } = useQuery<Platform[]>({ queryKey: ['platforms'], queryFn: listPlatforms })
  const inheritedPlatform = platforms.find(p => p.ID === device.PlatformID)

  const saveMut = useMutation({
    mutationFn: () => updateDeviceDNSFailover(device.ID, {
      FQDN: fqdn.trim(),
      UpstreamResolvers: resolversText.split(',').map(s => s.trim()).filter(Boolean),
    }),
    onSuccess: () => {
      setSaved(true)
      qc.invalidateQueries({ queryKey: ['device', device.ID] })
      setTimeout(() => setSaved(false), 3000)
    },
  })

  const inputSt: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box',
    padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14,
  }

  return (
    <div style={cardStyle}>
      <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>DNS Failover</h3>
      <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 20px' }}>
        Runs a small DNS service on this device that answers the firewall's last-resort
        SIP outbound-proxy hostname directly — many firewalls' built-in DNS proxies don't
        forward SRV queries reliably. Every other query is forwarded upstream. Leave the
        hostname blank to disable.
      </p>
      <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 20px', background: '#f9fafb', borderRadius: 6, padding: '8px 12px' }}>
        If this device has more than one network segment configured, each segment
        answers this same hostname with its own address automatically (split-horizon) —
        no extra config needed per segment.
      </p>

      {inheritedPlatform?.DNSFailoverFQDN && !fqdn && (
        <p style={{ fontSize: 12, color: '#1c855a', background: '#e6f4ec', borderRadius: 6, padding: '8px 12px', margin: '0 0 14px' }}>
          Inheriting from platform <b>{inheritedPlatform.Name}</b>: <b>{inheritedPlatform.DNSFailoverFQDN}</b>
          {inheritedPlatform.DNSUpstreamResolvers?.length ? ` (resolvers: ${inheritedPlatform.DNSUpstreamResolvers.join(', ')})` : ''}.
          Set a value below to override just this device.
        </p>
      )}

      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
        Hostname {inheritedPlatform?.DNSFailoverFQDN ? '(override)' : ''}
      </label>
      <input
        style={{ ...inputSt, marginBottom: 14 }}
        placeholder={inheritedPlatform?.DNSFailoverFQDN || 'localfailover.example.com'}
        value={fqdn}
        onChange={e => setFqdn(e.target.value)}
      />

      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
        Upstream Resolvers (comma-separated)
      </label>
      <input
        style={{ ...inputSt, marginBottom: 6 }}
        placeholder={inheritedPlatform?.DNSUpstreamResolvers?.join(', ') || '1.1.1.1, 1.0.0.1'}
        value={resolversText}
        onChange={e => setResolversText(e.target.value)}
      />
      <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 20px' }}>
        Order of precedence: a network segment's own override (Network tab) beats this
        device's value, which beats the linked platform's default (if any), which beats
        the agent's own default (1.1.1.1, 1.0.0.1). There's no target-IP field: the agent
        always answers with its own address automatically.
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          style={{ ...btnStyle, background: '#2563eb', color: '#fff' }}
          onClick={() => saveMut.mutate()}
          disabled={saveMut.isPending}
        >
          {saveMut.isPending ? 'Saving…' : 'Save DNS Failover'}
        </button>
        {saved && <span style={{ fontSize: 13, color: '#065f46' }}>Settings saved.</span>}
        {saveMut.isError && <span style={{ fontSize: 13, color: '#dc2626' }}>Save failed.</span>}
      </div>
    </div>
  )
}

function PerformanceCard({ device }: { device: Device }) {
  const qc = useQueryClient()
  const [children, setChildren] = useState(String(device.KamailioChildren || ''))
  const [shmMB, setShmMB] = useState(String(device.ShmMemoryMB || ''))
  const [pkgMB, setPkgMB] = useState(String(device.PkgMemoryMB || ''))
  const [saved, setSaved] = useState(false)

  const saveMut = useMutation({
    mutationFn: () => updateDevicePerformance(device.ID, {
      KamailioChildren: Number(children) || 0,
      ShmMemoryMB: Number(shmMB) || 0,
      PkgMemoryMB: Number(pkgMB) || 0,
    }),
    onSuccess: () => {
      setSaved(true)
      qc.invalidateQueries({ queryKey: ['device', device.ID] })
      setTimeout(() => setSaved(false), 3000)
    },
  })

  const inputSt: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box',
    padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14,
  }

  return (
    <div style={cardStyle}>
      <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>Performance</h3>
      <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 20px' }}>
        Tunes the SIP proxy's resource allocation for higher call/registration volume.
        Leave a field blank to use the platform default. Applying changes restarts
        the SIP proxy on the device.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 6 }}>
        <div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
            Worker Processes
          </label>
          <input
            style={inputSt}
            type="number"
            min={0}
            placeholder="default (4)"
            value={children}
            onChange={e => setChildren(e.target.value)}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
            Shared Memory (MB)
          </label>
          <input
            style={inputSt}
            type="number"
            min={0}
            placeholder="package default"
            value={shmMB}
            onChange={e => setShmMB(e.target.value)}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
            Private Memory (MB)
          </label>
          <input
            style={inputSt}
            type="number"
            min={0}
            placeholder="package default"
            value={pkgMB}
            onChange={e => setPkgMB(e.target.value)}
          />
        </div>
      </div>
      <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 20px' }}>
        For heavier registration/call volume, try Shared Memory 256MB and Private
        Memory 32MB as a starting point.
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          style={{ ...btnStyle, background: '#2563eb', color: '#fff' }}
          onClick={() => saveMut.mutate()}
          disabled={saveMut.isPending}
        >
          {saveMut.isPending ? 'Saving…' : 'Save Performance Settings'}
        </button>
        {saved && <span style={{ fontSize: 13, color: '#065f46' }}>Saved — push config to apply.</span>}
        {saveMut.isError && (
          <span style={{ fontSize: 13, color: '#dc2626' }}>Save failed.</span>
        )}
      </div>
    </div>
  )
}

// ConfigApplyStatus is the single source of truth for config-push state —
// queuing, waiting on the device, and the final applied/failed result all
// render through this one line instead of two separate indicators (a
// transient "queued" message by the button plus a separate persistent
// "last applied" line), which was confusing: they could disagree, and it
// wasn't clear which one reflected reality.
//
// "Queued" (the REST call succeeding) only means the server accepted the
// push request — it says nothing about whether the device applied it. That
// only becomes known once the device's ConfigAck comes back, which we detect
// by polling: device.LastConfigApplyVersion catching up to the version this
// push queued.
function ConfigApplyStatus({
  device,
  queuedVersion,
  isQueuing,
  queueFailed,
}: {
  device: Device
  queuedVersion: string | undefined
  isQueuing: boolean
  queueFailed: boolean
}) {
  if (isQueuing) {
    return <p style={{ margin: '6px 0 0', fontSize: 13, color: '#6b7280' }}>Sending config to device...</p>
  }
  if (queueFailed) {
    return <p style={{ margin: '6px 0 0', fontSize: 13, color: '#dc2626' }}>Failed to send config to device</p>
  }
  if (queuedVersion && device.LastConfigApplyVersion !== queuedVersion) {
    return <p style={{ margin: '6px 0 0', fontSize: 13, color: '#6b7280' }}>Waiting for device to confirm...</p>
  }
  if (device.LastConfigApplySuccess === null) {
    return null
  }
  const when = device.LastConfigApplyUnix
    ? new Date(device.LastConfigApplyUnix * 1000).toLocaleString()
    : 'unknown time'
  if (device.LastConfigApplySuccess) {
    return (
      <p style={{ margin: '6px 0 0', fontSize: 13, color: '#065f46' }}>
        Last config applied: v{device.LastConfigApplyVersion} at {when}
      </p>
    )
  }
  return (
    <p style={{ margin: '6px 0 0', fontSize: 13, color: '#dc2626' }}>
      Last config push FAILED at {when}: {device.LastConfigApplyError || 'unknown error'}
    </p>
  )
}

export default function DeviceDetail() {
  const { id } = useParams<{ id: string }>()
  const [tab, setTab] = useState<Tab>('overview')

  const { data: device, isLoading, error } = useQuery<Device>({
    queryKey: ['device', id],
    queryFn: () => getDevice(id!),
    refetchInterval: 15_000,
    enabled: !!id,
  })

  const qc = useQueryClient()
  const pushMut = useMutation({
    mutationFn: () => pushConfig(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['device', id] }),
  })

  if (isLoading) return <p style={{ color: '#6b7280' }}>Loading device...</p>
  if (error || !device) return <p style={{ color: '#dc2626' }}>Device not found.</p>

  const tabs: { key: Tab; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'ring-groups', label: 'Ring Groups' },
    { key: 'gsm-patterns', label: 'Cellular' },
    { key: 'network', label: 'Network' },
    { key: 'updates', label: 'Updates' },
    { key: 'settings', label: 'Settings' },
  ]

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <Link to="/" style={{ color: '#6b7280', textDecoration: 'none', fontSize: 13 }}>
          ← All Devices
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>{device.Name || device.ID}</h1>
            <StatusBadge status={device.Disabled ? 'disabled' : device.Status} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              style={{ ...btnStyle, background: '#2563eb', color: '#fff' }}
              onClick={() => pushMut.mutate()}
              disabled={pushMut.isPending}
            >
              {pushMut.isPending ? 'Pushing...' : 'Push Config Now'}
            </button>
          </div>
        </div>
        {device.Site && <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 14 }}>Site: {device.Site}</p>}
        <ConfigApplyStatus
          device={device}
          queuedVersion={pushMut.data?.config_version}
          isQueuing={pushMut.isPending}
          queueFailed={pushMut.isError}
        />
      </div>

      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #e5e7eb', marginBottom: 24 }}>
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '8px 20px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: tab === t.key ? 600 : 400,
              color: tab === t.key ? '#2563eb' : '#6b7280',
              borderBottom: tab === t.key ? '2px solid #2563eb' : '2px solid transparent',
              marginBottom: -2,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab device={device} />}
      {tab === 'ring-groups' && <RingGroupsTab deviceId={device.ID} />}
      {tab === 'gsm-patterns' && <GSMPatternsTab device={device} />}
      {tab === 'network' && <NetworkTab deviceId={device.ID} />}
      {tab === 'updates' && <UpdatesTab device={device} />}
      {tab === 'settings' && <SettingsTab device={device} />}
    </div>
  )
}
